// AboutSection.jsx
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import SectionHeading from "../UIComponents/SectionHeading";
import Skills from "./Skills";

gsap.registerPlugin(ScrollTrigger);

const AboutSection = () => {
    const aboutRef = useRef(null);
    const imgRef = useRef(null);
    const observerRef = useRef(null);
    const ctxRef = useRef(null);

    useEffect(() => {
        const aboutEl = aboutRef.current;
        if (!aboutEl) {
            console.warn("AboutSection: aboutRef is null");
            return;
        }

        ctxRef.current = gsap.context(() => {
            const textItems = gsap.utils.toArray(".about-anim", aboutEl);
            const imgItem = imgRef.current;

            // Set initial states
            gsap.set(textItems, { autoAlpha: 0, x: 80 });
            gsap.set(imgItem, { autoAlpha: 0, x: -50 });

            const animateAll = () => {
                gsap.to(textItems, {
                    autoAlpha: 1,
                    x: 0,
                    duration: 1.6,
                    ease: "power3.out",
                    stagger: 0.4,
                });

                if (imgItem) {
                    gsap.to(imgItem, {
                        autoAlpha: 1,
                        x: 0,
                        duration: 0.5,
                        ease: "power3.out",
                        stagger: 1.4,
                    });
                }
            };

            if (typeof ScrollTrigger !== "undefined" && textItems.length > 0) {
                ScrollTrigger.create({
                    trigger: ".about-content",
                    start: "top 75%",
                    once: true,
                    onEnter: () => {
                        animateAll();
                    }
                });

                setTimeout(() => {
                    ScrollTrigger.refresh();
                }, 300);
            } else {
                // Fallback
                const io = new IntersectionObserver((entries, obs) => {
                    if (entries[0].isIntersecting) {
                        animateAll();
                        obs.disconnect();
                    }
                });
                observerRef.current = io;
                io.observe(aboutEl);
            }
        }, aboutRef);


        return () => {
            // cleanup
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            if (ctxRef.current) ctxRef.current.revert();
        };
    }, []);

    // Ensure ScrollTrigger recalculates after image loaded
    const handleImgLoad = () => {
        try {
            ScrollTrigger.refresh();
        } catch (err) {
            console.warn("AboutSection: ScrollTrigger.refresh() error:", err);
        }
    };

    return (
        <section
            ref={aboutRef}
            id="about"
            className="relative w-full py-20 px-6 md:px-12 lg:px-20 xl:px-32 bg-background text-text-light"
        >
            <SectionHeading SectionNumber="01" SectionTitle="About Me." />

            <div className="about-content mb-10 grid grid-cols-1 lg:grid-cols-2 items-center gap-6 lg:gap-16">

                {/* IMAGE */}
                <div className="flex justify-center lg:justify-start">
                    <div className="relative w-full overflow-hidden shadow-xl group mb-5 lg:mb-0">

                        <img
                            ref={imgRef}
                            src="/Hero_Image.webp"
                            alt="Bhavya Kansara - Web Developer and Graphic Designer"
                            onLoad={handleImgLoad}
                            className="about-img-anim w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30"></div>
                    </div>
                </div>

                {/* TEXT */}
                <div className="relative text-white flex flex-col gap-4 overflow-hidden">
                    <p className="sr-only">
                        Bhavya Kansara is a professional web developer and graphic designer
                        specializing in clean website design, user experience, and modern
                        digital interfaces.
                    </p>
                    <p className="about-anim text-base md:text-lg leading-relaxed">
                        I’m Bhavya Kansara, a web developer and graphic designer specializing in
                        clean, user-friendly websites and digital experiences. I work at the intersection of development and design, building websites that are visually clear,
                        easy to navigate, and aligned with real user needs.
                    </p>
                    <p className="about-anim text-base md:text-lg leading-relaxed">
                        My approach is simple: design with purpose and build with clarity.
                        I believe great websites should feel intuitive, perform smoothly,
                        and communicate ideas without unnecessary complexity. Every project
                        I work on balances structure, usability, and visual identity.
                    </p>
                    <p className="about-anim text-base md:text-lg leading-relaxed">
                        With a Diploma in Computer Engineering from GTU, I bring a strong
                        technical foundation to my creative work. From responsive websites
                        to branding and visual design, I focus on delivering solutions that
                        are practical, consistent, and built to last.
                    </p>
                </div>
            </div>

            <Skills />
        </section>
    );
};

export default AboutSection;
